import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waCampaignService,
  WaCampaignFilters,
  CreateWaCampaignRequest,
  UpdateWaCampaignRequest,
  AddRecipientsRequest,
  RemoveRecipientsRequest,
} from '@services/wa-campaign.service'

export function useWaCampaigns(filters: WaCampaignFilters = {}) {
  return useQuery({
    queryKey: ['waCampaigns', filters],
    queryFn: () => waCampaignService.fetchCampaigns(filters),
  })
}

export function useWaCampaign(id: number) {
  return useQuery({
    queryKey: ['waCampaign', id],
    queryFn: () => waCampaignService.getCampaign(id),
    enabled: !!id,
  })
}

export function useWaCampaignStats(id: number) {
  return useQuery({
    queryKey: ['waCampaign', id, 'stats'],
    queryFn: () => waCampaignService.getStats(id),
    enabled: !!id,
    refetchInterval: 30000, // Refresh every 30 seconds for active campaigns
  })
}

export function useWaCampaignRecipients(id: number, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['waCampaign', id, 'recipients', params],
    queryFn: () => waCampaignService.getRecipients(id, params),
    enabled: !!id,
  })
}

export function useCreateWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateWaCampaignRequest) => waCampaignService.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
    },
  })
}

export function useUpdateWaCampaign(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateWaCampaignRequest) => waCampaignService.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id] })
    },
  })
}

export function useDeleteWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waCampaignService.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
    },
  })
}

export function useStartWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waCampaignService.startCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id] })
    },
  })
}

export function usePauseWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waCampaignService.pauseCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id] })
    },
  })
}

export function useResumeWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waCampaignService.resumeCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id] })
    },
  })
}

export function useCancelWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waCampaignService.cancelCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id] })
    },
  })
}

export function useAddWaCampaignRecipients(campaignId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AddRecipientsRequest) => waCampaignService.addRecipients(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waCampaign', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', campaignId, 'recipients'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', campaignId, 'stats'] })
    },
  })
}

export function useRemoveWaCampaignRecipients(campaignId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RemoveRecipientsRequest) => waCampaignService.removeRecipients(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waCampaign', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', campaignId, 'recipients'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', campaignId, 'stats'] })
    },
  })
}

export function useRetryWaCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waCampaignService.retryCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id, 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['waCampaign', id, 'recipients'] })
    },
  })
}
